import { type RedeemedCoupon } from "@/hooks/useRewards";

interface Props {
  coupon: RedeemedCoupon;
  onClose: () => void;
  onCopy: (code: string) => void;
  copiedCode: string | null;
  onShop: () => void;
}

export default function RedeemSuccessModal({ coupon, onClose, onCopy, copiedCode, onShop }: Props) {
  const isCopied = copiedCode === coupon.couponCode;
  const expiresDate = new Date(coupon.expiresAt).toLocaleDateString("ar-SA", {
    year: "numeric", month: "long", day: "numeric",
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" dir="rtl">
      <div className="bg-[#111] border border-green-400/25 rounded-3xl p-6 w-full max-w-sm relative overflow-hidden">
        {/* Top glow */}
        <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-green-400/0 via-green-400/60 to-green-400/0"></div>

        {/* Success animation */}
        <div className="w-16 h-16 flex items-center justify-center bg-green-400/15 border border-green-400/25 rounded-2xl mx-auto mb-5">
          <i className="ri-checkbox-circle-line text-green-400 text-3xl"></i>
        </div>

        <h3 className="text-white text-lg font-black text-center mb-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
          تم إنشاء الكوبون!
        </h3>
        <p className="text-gray-500 text-xs text-center mb-6" style={{ fontFamily: "'Cairo', sans-serif" }}>
          كوبون الخصم جاهز للاستخدام في طلبك القادم
        </p>

        {/* Coupon display */}
        <div className="bg-gradient-to-br from-green-900/20 to-gray-900/60 border border-green-400/25 rounded-2xl p-5 mb-5 text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-green-400/0 via-green-400/40 to-green-400/0"></div>

          <p className="text-green-400 text-4xl font-black mb-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
            {coupon.discountPercent}%
          </p>
          <p className="text-gray-400 text-xs mb-4" style={{ fontFamily: "'Cairo', sans-serif" }}>خصم على طلبك القادم</p>

          {/* Code box */}
          <div className="bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3 mb-3">
            <p className="font-mono text-white text-sm font-black tracking-widest">{coupon.couponCode}</p>
          </div>

          <button
            onClick={() => onCopy(coupon.couponCode)}
            className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
              isCopied
                ? "bg-green-400 text-black"
                : "bg-white/10 hover:bg-white/20 text-gray-300"
            }`}
            style={{ fontFamily: "'Cairo', sans-serif" }}
          >
            <i className={`text-sm ${isCopied ? "ri-check-double-line" : "ri-file-copy-line"}`}></i>
            {isCopied ? "تم النسخ ✓" : "نسخ الكوبون"}
          </button>
        </div>

        {/* Expiry info */}
        <div className="flex items-center justify-center gap-2 mb-5">
          <i className="ri-time-line text-amber-400/60 text-sm"></i>
          <p className="text-gray-500 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>
            صالح حتى: <span className="text-amber-400/80 font-bold">{expiresDate}</span>
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 border border-white/10 rounded-xl py-3 text-sm font-bold text-gray-400 hover:text-white hover:border-white/20 transition-all cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Cairo', sans-serif" }}
          >
            كوبوناتي
          </button>
          <button
            onClick={onShop}
            className="flex-1 bg-amber-400 hover:bg-amber-300 text-black rounded-xl py-3 text-sm font-black transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            style={{ fontFamily: "'Cairo', sans-serif" }}
          >
            <i className="ri-shopping-cart-2-line"></i>
            تسوق الآن
          </button>
        </div>
      </div>
    </div>
  );
}
